import { toast } from 'react-toastify';
import i18n from '../../lang/i18n';
import { uploadImage } from '../../services/Core/imageService';

const addResizeHandle = (wrapper, img, editor, onChange) => {
  const handle = document.createElement('div');
  handle.style.width = '10px';
  handle.style.height = '10px';
  handle.style.background = 'gray';
  handle.style.position = 'absolute';
  handle.style.right = '0';
  handle.style.bottom = '0';
  handle.style.cursor = 'se-resize';
  handle.className = 'resize-handle';

  let isResizing = false;

  handle.addEventListener('mousedown', (e) => {
    e.preventDefault();
    isResizing = true;

    const startX = e.clientX;
    const startWidth = img.offsetWidth;
    const aspectRatio = img.offsetHeight / img.offsetWidth;
    // A size picked as a share of the width (wrapper at N%, image at 100%) becomes pixels while dragging.
    if (wrapper.style.width) {
      wrapper.style.width = '';
      img.style.width = `${startWidth}px`;
    }

    const onMouseMove = (e) => {
      if (!isResizing) return;
      const dx = e.clientX - startX;
      const newWidth = Math.max(50, startWidth + dx);
      img.style.width = `${newWidth}px`;
      img.style.height = `${Math.round(newWidth * aspectRatio)}px`;
    };

    const onMouseUp = () => {
      isResizing = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      onChange?.(editor.innerHTML);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  });

  wrapper.appendChild(handle);
};

const insertImageAtCaret = (
  { url, reference_key, filename },
  editorRef,
  onChange,
) => {
  const editor = editorRef.current;
  // The editor was closed while the image was uploading.
  if (!editor) return;

  const wrapper = document.createElement('div');
  wrapper.contentEditable = false;
  wrapper.style.position = 'relative';
  wrapper.style.display = 'inline-block';
  wrapper.style.margin = '5px';
  wrapper.className = 'img-wrapper';

  const img = document.createElement('img');
  img.src = url;
  img.setAttribute('data-ref-key', reference_key);
  img.alt = filename;
  img.style.width = '200px';
  img.style.height = 'auto';
  img.style.display = 'block';

  wrapper.appendChild(img);
  addResizeHandle(wrapper, img, editor, onChange);

  const selection = window.getSelection();
  const isInside =
    selection &&
    selection.rangeCount > 0 &&
    editor.contains(selection.anchorNode);

  if (isInside) {
    const range = selection.getRangeAt(0);
    range.deleteContents();
    range.insertNode(wrapper);
    range.setStartAfter(wrapper);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  } else {
    editor.appendChild(wrapper);
  }

  editor.appendChild(document.createElement('br'));
  onChange?.(editor.innerHTML);
};

export const attachResizeHandlesToExistingImages = (editorRef, onChange) => {
  const editor = editorRef.current;
  if (!editor) return;

  const images = editor.querySelectorAll('img');
  images.forEach((img) => {
    let wrapper = img.closest('.img-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.contentEditable = false;
      wrapper.style.position = 'relative';
      wrapper.style.display = 'inline-block';
      wrapper.style.margin = '5px';
      wrapper.className = 'img-wrapper';
      img.parentNode.insertBefore(wrapper, img);
      wrapper.appendChild(img);
    }

    const existingHandle = wrapper.querySelector('.resize-handle');
    if (existingHandle) existingHandle.remove();

    img.style.display = 'block';
    addResizeHandle(wrapper, img, editor, onChange);
  });
};

const MAX_IMAGE_BYTES = 1024 * 1024 * 5;

// Uploads one image file and inserts it at the caret (or at the end). Resolves when it is done or failed.
export const uploadImageFile = (file, editorRef, onChange) => {
  if (file.size > MAX_IMAGE_BYTES) {
    toast.error(i18n.t('my_questions.editor.image_too_large'));
    return Promise.resolve();
  }
  const formData = new FormData();
  formData.append('image', file);
  return uploadImage(formData)
    .then((res) => {
      insertImageAtCaret(res, editorRef, onChange);
    })
    .catch((err) => {
      toast.error(i18n.t('my_questions.editor.image_upload_failed'));
      console.log(err);
    });
};

export const handleImageUpload = (e, editorRef, setIsUploading, onChange) => {
  const file = e.target.files[0];
  if (!file) return;
  setIsUploading(true);
  uploadImageFile(file, editorRef, onChange).finally(() => {
    setIsUploading(false);
  });

  e.target.value = null;
};

// After a paste: images that came as data: URIs (refused by the server) are uploaded and get a reference;
// file:// or blob: ones (Word's local copies, which a page cannot read) are taken out. Resolves with the
// number of images taken out.
export const settlePastedImages = async (editorRef, onChange) => {
  const editor = editorRef.current;
  if (!editor) return 0;
  let dropped = 0;
  const pasted = [...editor.querySelectorAll('img')].filter((img) => !img.getAttribute('data-ref-key'));
  for (const img of pasted) {
    const src = img.getAttribute('src') || '';
    if (/^https?:/i.test(src)) continue;
    let uploaded = false;
    if (src.startsWith('data:')) {
      try {
        const blob = await (await fetch(src)).blob();
        if (blob.size <= MAX_IMAGE_BYTES) {
          const formData = new FormData();
          formData.append('image', new File([blob], `pasted.${(blob.type.split('/')[1] || 'png').split('+')[0]}`, { type: blob.type }));
          const res = await uploadImage(formData);
          img.src = res.url;
          img.setAttribute('data-ref-key', res.reference_key);
          img.removeAttribute('srcset');
          uploaded = true;
        }
      } catch (err) {
        console.log(err);
      }
    }
    if (!uploaded) {
      (img.closest('.img-wrapper') || img).remove();
      dropped += 1;
    }
  }
  if (pasted.length) {
    attachResizeHandlesToExistingImages(editorRef, onChange);
    onChange?.(editor.innerHTML);
  }
  return dropped;
};

// Puts the caret where a file is dropped, so the picture lands there.
export const placeCaretAtPoint = (x, y) => {
  const range = document.caretRangeFromPoint?.(x, y);
  if (!range) return;
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
};
